import { useState } from 'react';
import './FacetedSearch.css';

const FacetedSearch = ({ isOpen, onClose, onApplyFilters }) => {
  const [filters, setFilters] = useState({
    status: {
      delivered: { checked: false, count: 854 },
      inTransit: { checked: false, count: 626 },
      pending: { checked: false, count: 484 },
      delayed: { checked: false, count: 142 },
      failed: { checked: false, count: 28 }
    },
    productCategory: {
      electronics: { checked: false, count: 683 },
      apparel: { checked: false, count: 598 },
      foodBeverage: { checked: false, count: 484 },
      industrial: { checked: false, count: 370 },
      pharmaceuticals: { checked: false, count: 142 },
      consumerGoods: { checked: false, count: 313 },
      other: { checked: false, count: 199 }
    },
    priority: {
      critical: { checked: false, count: 89 },
      high: { checked: false, count: 234 },
      medium: { checked: false, count: 456 },
      low: { checked: false, count: 678 }
    },
    dateRange: {
      today: { checked: false, count: 342 },
      yesterday: { checked: false, count: 298 },
      thisWeek: { checked: false, count: 1456 },
      lastWeek: { checked: false, count: 1234 },
      thisMonth: { checked: false, count: 2847 }
    }
  });

  const [expandedSections, setExpandedSections] = useState({
    status: true,
    productCategory: true,
    priority: false,
    dateRange: false
  });

  const [searchTerm, setSearchTerm] = useState('');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFilterToggle = (category, item) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [item]: {
          ...prev[category][item],
          checked: !prev[category][item].checked
        }
      }
    }));
  };

  const clearAllFilters = () => {
    const clearedFilters = {};
    Object.keys(filters).forEach(category => {
      clearedFilters[category] = {};
      Object.keys(filters[category]).forEach(item => {
        clearedFilters[category][item] = {
          ...filters[category][item],
          checked: false
        };
      });
    });
    setFilters(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    Object.values(filters).forEach(category => {
      Object.values(category).forEach(item => {
        if (item.checked) count++;
      });
    });
    return count;
  };

  const applyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  const formatLabel = (key) => {
    const labels = {
      delivered: 'Delivered',
      inTransit: 'In Transit',
      pending: 'Pending',
      delayed: 'Delayed',
      failed: 'Failed',
      electronics: 'Electronics',
      apparel: 'Apparel',
      foodBeverage: 'Food & Beverage',
      industrial: 'Industrial',
      pharmaceuticals: 'Pharmaceuticals',
      consumerGoods: 'Consumer Goods',
      other: 'Other',
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      thisMonth: 'This Month'
    };
    return labels[key] || key;
  };

  const activeCount = getActiveFilterCount();

  return (
    <>
      {/* Overlay with centered panel */}
      {isOpen && (
        <div className="faceted-overlay" onClick={onClose}>
          <div
            className={`faceted - search - panel ${isOpen ? 'open' : ''} `}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="faceted-header">
              <div className="faceted-title-row">
                <h3>Faceted Search</h3>
                <button className="faceted-close" onClick={onClose}>×</button>
              </div>
              <div className="faceted-results">
                2,847 results found
              </div>
              {activeCount > 0 && (
                <button className="clear-all-btn" onClick={clearAllFilters}>
                  Clear all filters ({activeCount})
                </button>
              )}
            </div>

            <div className="faceted-search-bar">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="faceted-search-input"
              />
              <svg className="faceted-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>

            <div className="faceted-content">
              {/* Status Section */}
              <div className="faceted-section">
                <div className="faceted-section-header" onClick={() => toggleSection('status')}>
                  <span className="faceted-section-title">Status</span>
                  <svg
                    className={`faceted - chevron ${expandedSections.status ? 'expanded' : ''} `}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                {expandedSections.status && (
                  <div className="faceted-section-content">
                    {Object.entries(filters.status).map(([key, value]) => (
                      <label key={key} className="faceted-checkbox-item">
                        <input
                          type="checkbox"
                          checked={value.checked}
                          onChange={() => handleFilterToggle('status', key)}
                        />
                        <span className="faceted-checkbox-label">
                          {formatLabel(key)}
                        </span>
                        <span className="faceted-count">{value.count}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Category Section */}
              <div className="faceted-section">
                <div className="faceted-section-header" onClick={() => toggleSection('productCategory')}>
                  <span className="faceted-section-title">Product Category</span>
                  <svg
                    className={`faceted - chevron ${expandedSections.productCategory ? 'expanded' : ''} `}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                {expandedSections.productCategory && (
                  <div className="faceted-section-content">
                    {Object.entries(filters.productCategory).map(([key, value]) => (
                      <label key={key} className="faceted-checkbox-item">
                        <input
                          type="checkbox"
                          checked={value.checked}
                          onChange={() => handleFilterToggle('productCategory', key)}
                        />
                        <span className="faceted-checkbox-label">
                          {formatLabel(key)}
                        </span>
                        <span className="faceted-count">{value.count}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Priority Section */}
              <div className="faceted-section">
                <div className="faceted-section-header" onClick={() => toggleSection('priority')}>
                  <span className="faceted-section-title">Priority</span>
                  <svg
                    className={`faceted - chevron ${expandedSections.priority ? 'expanded' : ''} `}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                {expandedSections.priority && (
                  <div className="faceted-section-content">
                    {Object.entries(filters.priority).map(([key, value]) => (
                      <label key={key} className="faceted-checkbox-item">
                        <input
                          type="checkbox"
                          checked={value.checked}
                          onChange={() => handleFilterToggle('priority', key)}
                        />
                        <span className="faceted-checkbox-label">
                          {formatLabel(key)}
                        </span>
                        <span className="faceted-count">{value.count}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Range Section */}
              <div className="faceted-section">
                <div className="faceted-section-header" onClick={() => toggleSection('dateRange')}>
                  <span className="faceted-section-title">Date Range</span>
                  <svg
                    className={`faceted - chevron ${expandedSections.dateRange ? 'expanded' : ''} `}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                {expandedSections.dateRange && (
                  <div className="faceted-section-content">
                    {Object.entries(filters.dateRange).map(([key, value]) => (
                      <label key={key} className="faceted-checkbox-item">
                        <input
                          type="checkbox"
                          checked={value.checked}
                          onChange={() => handleFilterToggle('dateRange', key)}
                        />
                        <span className="faceted-checkbox-label">
                          {formatLabel(key)}
                        </span>
                        <span className="faceted-count">{value.count}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="faceted-footer">
              <button className="faceted-btn secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="faceted-btn primary" onClick={applyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FacetedSearch;
